import type { Theme } from "@mui/material/styles";

/**
 * The shop's fixed chrome — the header and the sticky bottom bars (price,
 * calendar navigation) — in dark mode.
 *
 * These were hard-coded light grey (#f5f5f5) in dark mode: a bright strip
 * top and bottom of a near-black page, worst on a phone where the bars take
 * a large share of the screen. Dark mode now uses one deep teal-ink surface,
 * so the chrome reads as the brand at night instead of as a light theme
 * leaking through. Light mode is untouched: every helper returns the value
 * the caller passes for light, exactly as before.
 */
export const DARK_CHROME = {
  /** Deep teal-ink: the brand's hue at night strength, a step above the page. */
  surface: "#0F2A33",
  /** Text and icons on the surface — the dark theme's ink. */
  text: "#F2EEE8",
  /** Hover/active accents — the dark theme's brand teal. */
  accent: "#3FAFD0",
} as const;

const isDark = (theme: Theme) => theme.palette.mode === "dark";

/** The chrome's background: `light` in light mode, the teal-ink in dark. */
export const chromeSurface = (theme: Theme, light: string): string =>
  isDark(theme) ? DARK_CHROME.surface : light;

/** Text/icon colour on the chrome: `light` in light mode. */
export const chromeText = (theme: Theme, light: string): string =>
  isDark(theme) ? DARK_CHROME.text : light;
